/**
 * Who draws a leaf (RFC 0284 §4b, S6).
 *
 * A document leaf already asks the catalog this question: `projectApps(ws).find(
 * a => a.handles?.includes(file.mime))`, so an app declaring
 * `handles: ['text/markdown']` replaces the built-in editor. A session leaf asks
 * nobody, because `agent` content has exactly one renderer and no way to name
 * another. That asymmetry is the only thing making the agent panel feel like
 * furniture rather than a pane.
 *
 * So content KIND joins the same key space as mime. `handles: ['agent']` is a
 * thread app; `handles: ['text/markdown']` is an editor; one lookup answers
 * both, on the thin catalog, with no app document hydrated.
 *
 * WHY A KEY AND NOT A SECOND FIELD. An `AppRef.renders?: ContentKind[]` beside
 * `handles` would be a second list to keep in step with the first, and every
 * caller would have to know which of the two to consult. A mime and a content
 * kind are both just "what this leaf is", and a string union of the two is
 * unambiguous because no mime is spelled `agent`.
 *
 * ONE RULE FOR BOTH, since S6m. `handlerFor` was a `find` over `handles` and
 * `DocumentView` had its own identical line over the PROJECT catalog only,
 * which meant a World-installed editor was never found for a document at all.
 * Both go through `resolveHandler` now, which knows about a person's choice.
 */

import { type AppSummary, resolveHandler } from '@mod/core/model'
import type { ReactNode } from 'react'
import type { AgentRendererProps } from './agent/agentRenderer'

/**
 * What a session leaf is keyed by. The literal content kind, matching
 * `AgentContent['kind']`, so the catalog and the content tree spell it the same
 * way and a typo cannot silently mean "handles nothing".
 */
export const AGENT_RENDERER_KEY = 'agent'

/** Content kinds that resolve a renderer. `picker` and `terminal` are not here:
 *  a picker has no subject to hand anybody, and a terminal is a live connection
 *  to a machine rather than a document an app could draw. */
export type RenderableKind = 'agent'

/** Precise source mimes compatible with the text/plain Code renderer. */
const CODE_TEXT_MIMES = new Set([
  'text/javascript',
  'application/javascript',
  'text/typescript',
  'application/typescript',
  'application/json',
  'text/css',
  'text/html',
  'application/xml',
  'text/xml',
  'application/yaml',
  'text/yaml',
  'text/x-python',
  'text/x-rust',
  'text/x-go',
  'text/x-sql',
  'text/x-shellscript',
])

/** What each renderable kind hands its renderer, per slot's `SlotPropsMap`. */
export interface RendererPropsMap {
  agent: AgentRendererProps
}

/**
 * The app that opens a key, or undefined for the built-in (RFC 0284 S6m).
 *
 * Read off the THIN catalog, never by hydrating: opening any leaf asks this,
 * and paying an app-doc load per installed app to answer it would undo the
 * property the catalog exists for.
 *
 * `handlers` is the person's choice, from the World. Several apps may claim one
 * key now — registration stopped refusing that, because refusing it made trying
 * a different editor mean uninstalling the one you had. `resolveHandler` picks:
 * a choice wins, a lone claimant wins, and several claimants with no choice get
 * the built-in rather than a coin flip.
 */
export function handlerFor(
  apps: AppSummary[],
  key: string,
  handlers?: Record<string, string>,
): AppSummary | undefined {
  const exact = resolveHandler(apps, key, handlers).app
  if (exact || !CODE_TEXT_MIMES.has(key)) return exact
  // Compatibility for Code installations registered before their handles
  // widened: existing app-source documents should improve immediately.
  return resolveHandler(apps, 'text/plain', handlers).app
}

/** A renderer bundled with the shell, for one content kind. */
export interface BuiltinRenderer<K extends RenderableKind = RenderableKind> {
  kind: K
  /** Stable id, so an arrangement can name it the way it names a slot fill. */
  id: string
  title: string
  render: (props: RendererPropsMap[K]) => ReactNode
}

type StoredRenderer = Omit<BuiltinRenderer, 'render' | 'kind'> & {
  kind: RenderableKind
  // `never` for the same reason the slot store uses it: every renderer's props
  // is assignable to it, so registration needs no cast and the key is the proof
  // on the way out.
  render: (props: never) => ReactNode
}

const renderers = new Map<RenderableKind, StoredRenderer>()

/**
 * Register the built-in for a kind.
 *
 * ONE per kind, unlike a slot, which holds a list. A slot is a surface several
 * apps can contribute sections to; a leaf shows one thing and is drawn by one
 * renderer. Re-registering replaces, which is what a hot reload does.
 */
export function registerRenderer<K extends RenderableKind>(
  renderer: BuiltinRenderer<K>,
): void {
  renderers.set(renderer.kind, renderer as StoredRenderer)
}

/** The built-in for a kind, or undefined if nothing registered it. */
export function builtinRendererFor<K extends RenderableKind>(
  kind: K,
): BuiltinRenderer<K> | undefined {
  return renderers.get(kind) as unknown as BuiltinRenderer<K> | undefined
}

/** Test seam, as with the slot registry: the map is module state. */
export function resetRenderers(): void {
  renderers.clear()
}

/**
 * Who draws this kind: the installed claimant if there is one, otherwise the
 * built-in.
 *
 * Returns a TAGGED result rather than a component, because the two are mounted
 * differently and the caller has to know which it got: a built-in is a React
 * render, an installed app is a view document in a sandbox with a bridge. A
 * union makes the caller handle both; a component would let it forget.
 */
export type RendererChoice<K extends RenderableKind> =
  | { source: 'installed'; app: AppSummary }
  | { source: 'builtin'; renderer: BuiltinRenderer<K> }
  | { source: 'none' }

export function resolveRenderer<K extends RenderableKind>(
  kind: K,
  key: string,
  apps: AppSummary[],
  handlers?: Record<string, string>,
): RendererChoice<K> {
  const installed = handlerFor(apps, key, handlers)
  if (installed) return { source: 'installed', app: installed }
  const builtin = builtinRendererFor(kind)
  return builtin ? { source: 'builtin', renderer: builtin } : { source: 'none' }
}
